import React, { useCallback, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import {
  CheckCircle as ActiveIcon,
  Add as AddIcon,
  PauseCircleOutlined as DeactivateIcon,
  Edit as EditIcon,
  Cancel as ExpiredIcon,
  DoNotDisturb as InactiveIcon,
  Schedule as ScheduledIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import { POINTS_PURCHASE_CAMPAIGN_TYPES } from '~/consts/luxLoyalty';

import { useGetPointsPurchaseOfferCampaigns } from '~/hooks/apis/loyalty/useGetPointsPurchaseOfferCampaigns';
import { useGetPointsPurchaseOffers } from '~/hooks/apis/loyalty/useGetPointsPurchaseOffers';
import { useUpdatePointsPurchaseOfferCampaign } from '~/hooks/apis/loyalty/usePointsPurchaseMutations';

import type { PointsPurchaseOffer, PointsPurchaseOfferCampaign } from '~/services/LuxLoyalty';

import getAllCurrencyCodes from '~/utils/getAllCurrencyCodes';

import CreateCampaignDialog from './CreateCampaignDialog';
import DeactivateConfirmDialog from './DeactivateConfirmDialog';
import EditCampaignDialog from './EditCampaignDialog';
import PayForPointsToolbar from './PayForPointsToolbar';
import { formatCampaignScope, formatCampaignType } from './utils';

function getCampaignStatusChipColor(status: string): 'success' | 'default' | 'info' | 'error' {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'SCHEDULED':
      return 'info';
    case 'EXPIRED':
      return 'error';
    default:
      return 'default';
  }
}

const DEFAULT_PAGE_SIZE = 25;

export default function CampaignsSection() {
  const theme = useTheme();

  const updateMutation = useUpdatePointsPurchaseOfferCampaign();

  const { data: offersData } = useGetPointsPurchaseOffers();
  const offers: Array<PointsPurchaseOffer> = useMemo(() => offersData?.result ?? [], [offersData]);

  const offersMap = useMemo(() => {
    const map: Record<string, PointsPurchaseOffer> = {};
    for (const offer of offers) {
      map[offer.id] = offer;
    }
    return map;
  }, [offers]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [offerFilter, setOfferFilter] = useState<string>('');
  const [currencyFilter, setCurrencyFilter] = useState<string>('');

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<PointsPurchaseOfferCampaign | null>(null);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    offerId: false,
    createdAt: false,
    updatedAt: false,
  });

  const { data, isLoading, error } = useGetPointsPurchaseOfferCampaigns();
  const allCampaigns: Array<PointsPurchaseOfferCampaign> = useMemo(() => data?.result ?? [], [data]);

  const statusCounts = useMemo(() => {
    const byStatus: Record<PointsPurchaseOfferCampaign['status'], number> = {
      ACTIVE: 0,
      INACTIVE: 0,
      SCHEDULED: 0,
      EXPIRED: 0,
    };
    for (const campaign of allCampaigns) {
      if (campaign.status in byStatus) {
        byStatus[campaign.status]++;
      }
    }
    return { ...byStatus, total: allCampaigns.length };
  }, [allCampaigns]);

  const filteredCampaigns = useMemo(() => {
    return allCampaigns.filter((campaign) => {
      if (statusFilter !== 'all' && campaign.status !== statusFilter) {
        return false;
      }
      if (typeFilter && campaign.type !== typeFilter) {
        return false;
      }
      if (offerFilter && campaign.offerId !== offerFilter) {
        return false;
      }
      if (currencyFilter && campaign.currency !== currencyFilter) {
        return false;
      }
      return true;
    });
  }, [allCampaigns, statusFilter, typeFilter, offerFilter, currencyFilter]);

  const handleClearFilters = useCallback(() => {
    setStatusFilter('all');
    setTypeFilter('');
    setOfferFilter('');
    setCurrencyFilter('');
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  }, []);

  const handleOpenEdit = useCallback((campaign: PointsPurchaseOfferCampaign) => {
    setSelectedCampaign(campaign);
    setIsEditOpen(true);
  }, []);

  const handleOpenDeactivate = useCallback((campaign: PointsPurchaseOfferCampaign) => {
    setSelectedCampaign(campaign);
    setIsDeactivateOpen(true);
  }, []);

  const handleConfirmDeactivate = useCallback(async () => {
    if (!selectedCampaign) {
      return;
    }
    try {
      await updateMutation.mutateAsync({
        campaignId: selectedCampaign.id,
        params: { status: 'INACTIVE' },
      });
      setIsDeactivateOpen(false);
    } catch {
      // Error handled in mutation hook
    }
  }, [selectedCampaign, updateMutation]);

  const formatScope = useCallback(
    (campaign: PointsPurchaseOfferCampaign) => formatCampaignScope(campaign, offersMap),
    [offersMap],
  );

  const columns: Array<GridColDef<PointsPurchaseOfferCampaign>> = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 180 },
      {
        field: 'type',
        headerName: 'Type',
        width: 160,
        display: 'flex',
        renderCell: ({ value }: GridRenderCellParams<PointsPurchaseOfferCampaign>) => (
          <Chip
            label={formatCampaignType(value)}
            size="small"
            variant={value === 'BONUS_POINTS' ? 'filled' : 'outlined'}
            color={value === 'BONUS_POINTS' ? 'secondary' : 'default'}
          />
        ),
      },
      {
        field: 'value',
        headerName: 'Value',
        width: 100,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'scope',
        headerName: 'Scope',
        width: 240,
        sortable: false,
        filterable: false,
        display: 'flex',
        renderCell: ({ row }: GridRenderCellParams<PointsPurchaseOfferCampaign>) => {
          const scopeInfo = formatScope(row);
          return <Chip label={scopeInfo.label} size="small" variant={scopeInfo.variant} />;
        },
      },
      { field: 'offerId', headerName: 'Offer ID', width: 140 },
      {
        field: 'currency',
        headerName: 'Target Currency',
        width: 140,
        renderCell: ({ value }: GridRenderCellParams<PointsPurchaseOfferCampaign>) => value || '-',
      },
      {
        field: 'startDate',
        headerName: 'Start',
        width: 150,
        valueFormatter: (value: string | null) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '-'),
      },
      {
        field: 'endDate',
        headerName: 'End',
        width: 150,
        sortComparator: (a: string | null, b: string | null) => {
          if (!a && !b) {
            return 0;
          }
          if (!a) {
            return 1;
          }
          if (!b) {
            return -1;
          }
          return dayjs(a).valueOf() - dayjs(b).valueOf();
        },
        renderCell: ({ value }: GridRenderCellParams<PointsPurchaseOfferCampaign>) => {
          if (!value) {
            return '-';
          }
          const isExpired = dayjs(value).isBefore(dayjs());
          return (
            <Typography component="span" sx={{ color: isExpired ? 'error.main' : 'success.main' }}>
              {dayjs(value).format('DD/MM/YYYY HH:mm')}
            </Typography>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 130,
        valueFormatter: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 130,
        valueFormatter: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 200,
        renderCell: ({ row }: GridRenderCellParams<PointsPurchaseOfferCampaign>) => {
          const showDaysLeft = row.status === 'ACTIVE' && row.endDate && dayjs(row.endDate).isAfter(dayjs());

          let daysLeftLabel = '';
          if (showDaysLeft) {
            const daysLeft = Math.ceil(dayjs(row.endDate).diff(dayjs(), 'hour') / 24);
            daysLeftLabel = daysLeft <= 0 ? '<1d left' : `${daysLeft}d left`;
          }

          return (
            <Stack direction="row" spacing={0.5} alignItems="center" height="100%">
              <Chip label={row.status} size="small" color={getCampaignStatusChipColor(row.status)} />
              {showDaysLeft && (
                <Chip
                  label={daysLeftLabel}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.15),
                    color: theme.palette.warning.dark,
                    fontWeight: theme.typography.fontWeightMedium,
                    fontSize: theme.typography.caption.fontSize,
                  }}
                />
              )}
            </Stack>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: GridRenderCellParams<PointsPurchaseOfferCampaign>) => {
          const canDeactivate = row.status === 'ACTIVE' || row.status === 'SCHEDULED';
          return (
            <Stack direction="row" spacing={0.5} alignItems="center" height="100%">
              <Tooltip title="Edit campaign">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenEdit(row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canDeactivate && (
                <Tooltip title="Deactivate campaign">
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenDeactivate(row);
                    }}
                  >
                    <DeactivateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    [theme, formatScope, handleOpenEdit, handleOpenDeactivate],
  );

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 'bold' }}>
              Offer Campaigns
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage time-limited promotions (price discounts and bonus points) applied to offers
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateOpen(true)}>
            Create Campaign
          </Button>
        </Box>
      </Paper>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: theme.typography.fontWeightBold }}>
            Status:
          </Typography>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_e, newValue) => {
              if (newValue !== null) {
                setStatusFilter(newValue);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                '&.Mui-selected': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.15),
                  color: 'primary.main',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.25),
                  },
                },
                '&:hover': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                },
              },
            }}
          >
            <ToggleButton value="all">All ({statusCounts.total})</ToggleButton>
            <ToggleButton value="ACTIVE">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ActiveIcon sx={{ fontSize: 'body2.fontSize', color: 'success.main' }} />
                <Typography variant="body2" component="span">
                  Active ({statusCounts.ACTIVE})
                </Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton value="SCHEDULED">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ScheduledIcon sx={{ fontSize: 'body2.fontSize', color: 'info.main' }} />
                <Typography variant="body2" component="span">
                  Scheduled ({statusCounts.SCHEDULED})
                </Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton value="EXPIRED">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ExpiredIcon sx={{ fontSize: 'body2.fontSize', color: 'grey.600' }} />
                <Typography variant="body2" component="span">
                  Expired ({statusCounts.EXPIRED})
                </Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton value="INACTIVE">
              <Stack direction="row" spacing={0.5} alignItems="center">
                <InactiveIcon sx={{ fontSize: 'body2.fontSize', color: 'grey.400' }} />
                <Typography variant="body2" component="span">
                  Inactive ({statusCounts.INACTIVE})
                </Typography>
              </Stack>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            select
            label="Type"
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Types</MenuItem>
            {POINTS_PURCHASE_CAMPAIGN_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {formatCampaignType(t)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Offer"
            value={offerFilter}
            onChange={(event) => setOfferFilter(event.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All Offers</MenuItem>
            {offers.map((offer) => (
              <MenuItem key={offer.id} value={offer.id}>
                {offer.title} ({offer.basePoints.toLocaleString()} pts)
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Target Currency"
            value={currencyFilter}
            onChange={(event) => setCurrencyFilter(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Currencies</MenuItem>
            {getAllCurrencyCodes().map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="text" onClick={handleClearFilters}>
            Clear
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Box mb={3}>
          <Alert severity="error">
            Error loading campaigns: {error instanceof Error ? error.message : String(error)}
          </Alert>
        </Box>
      )}

      <Paper elevation={0} sx={{ height: 650 }}>
        <DataGridPro
          rows={filteredCampaigns}
          columns={columns}
          loading={isLoading}
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          initialState={{
            sorting: { sortModel: [{ field: 'endDate', sort: 'desc' }] },
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          slots={{ toolbar: PayForPointsToolbar }}
          sx={{
            height: '100%',
            '& .MuiDataGrid-row:hover': {
              bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            },
          }}
        />
      </Paper>

      <CreateCampaignDialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} offers={offers} />

      {selectedCampaign && (
        <EditCampaignDialog
          open={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
            setSelectedCampaign(null);
          }}
          campaign={selectedCampaign}
          offersMap={offersMap}
        />
      )}

      <DeactivateConfirmDialog
        open={isDeactivateOpen}
        onClose={() => {
          setIsDeactivateOpen(false);
          setSelectedCampaign(null);
        }}
        onConfirm={handleConfirmDeactivate}
        isPending={updateMutation.isPending}
        entityType="campaign"
        entityLabel={selectedCampaign ? formatScope(selectedCampaign).label : ''}
      />
    </Box>
  );
}
